import { motion } from "framer-motion";
import { BrainCircuit, Layers, Wallet, Smartphone, Gauge } from "lucide-react";
import { Kicker, MaskLines, Rise, SlideShell } from "@/deck/ui";

const pillars = [
  { icon: BrainCircuit, title: "AI-native", text: "Intent → action. The assistant is the interface." },
  { icon: Layers, title: "Multi-BM architecture", text: "Many buildings, isolated data, one platform." },
  { icon: Wallet, title: "Multi payment gateway", text: "QRIS, virtual account, e-wallet — routed." },
  { icon: Smartphone, title: "PWA, mobile-ready", text: "Installs from a link. No app-store friction." },
];

const Box = ({ children, className = "", delay = 0, testId }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
    className={`rounded-2xl border border-white/10 bg-[var(--panel)] px-4 py-3 text-center ${className}`}
    data-testid={testId}
  >
    {children}
  </motion.div>
);

const Flow = () => (
  <div className="mx-auto h-7 w-px overflow-hidden bg-white/10">
    <div className="flow-line h-full w-full" />
  </div>
);

const Tag = ({ children }) => (
  <span className="rounded-full bg-white/[0.06] px-2.5 py-1 font-mono-d text-[11px] text-zinc-300">{children}</span>
);

export default function Tech() {
  return (
    <SlideShell testId="tech-slide">
      <div className="grid flex-1 items-center gap-8 lg:min-h-0 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <Rise><Kicker index="06">Innovation & technology</Kicker></Rise>
          <MaskLines lines={["AI-native,", "from the meter up."]} accentIndex={1} className="mt-4 text-4xl font-extrabold leading-[0.95] tracking-[-0.035em] sm:text-5xl lg:text-6xl" />
          <div className="mt-8 grid gap-3 sm:grid-cols-2">
            {pillars.map((p, i) => (
              <Rise key={p.title} delay={0.35 + i * 0.08} className="rounded-2xl border border-white/10 p-4" data-testid={`tech-pillar-${i + 1}`}>
                <p.icon size={20} className="text-[var(--dash)]" />
                <p className="mt-3 text-sm font-semibold text-zinc-50">{p.title}</p>
                <p className="mt-1 text-xs leading-relaxed text-zinc-400">{p.text}</p>
              </Rise>
            ))}
          </div>
        </div>
        <div className="lg:col-span-7" data-testid="tech-architecture-diagram">
          <div className="grid grid-cols-3 gap-3">
            {["Tenant PWA", "BM Dashboard", "Kasir Digital"].map((c, i) => (
              <Box key={c} delay={0.3 + i * 0.06}><p className="text-sm text-zinc-100">{c}</p></Box>
            ))}
          </div>
          <Flow />
          <Box delay={0.5} className="relative overflow-hidden border-dash/50 bg-dash/10 py-5" testId="tech-core">
            <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-dash/30 blur-3xl" />
            <p className="font-mono-d text-[11px] uppercase tracking-[0.24em] text-[var(--dash)]">Dash AI core</p>
            <p className="mt-1 font-display text-2xl font-bold text-zinc-50">LLM agent · intent → tools → action</p>
            <div className="mt-3 flex flex-wrap justify-center gap-2">
              <Tag>chat</Tag><Tag>voice & video</Tag><Tag>reminders</Tag><Tag>billing</Tag><Tag>E2E messaging</Tag>
            </div>
          </Box>
          <div className="grid grid-cols-2 gap-3">
            <Flow /><Flow />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Box delay={0.65}>
              <p className="font-mono-d text-[11px] uppercase tracking-[0.18em] text-zinc-400">Multi-BM tenancy</p>
              <div className="mt-2 flex justify-center gap-2"><Tag>BM A</Tag><Tag>BM B</Tag><Tag>BM n</Tag></div>
            </Box>
            <Box delay={0.72}>
              <p className="font-mono-d text-[11px] uppercase tracking-[0.18em] text-zinc-400">Payment router</p>
              <div className="mt-2 flex justify-center gap-2"><Tag>QRIS</Tag><Tag>VA</Tag><Tag>E-wallet</Tag></div>
            </Box>
          </div>
          <Flow />
          <Box delay={0.85} className="flex items-center justify-center gap-3">
            <Gauge size={18} className="text-[var(--dash)]" />
            <p className="text-sm text-zinc-100">Metrology-certified prepaid kWh meters · 1-phase & 3-phase</p>
          </Box>
        </div>
      </div>
    </SlideShell>
  );
}
